import { useMemo } from 'react';
import { useSuspenseMaxTagsQuery } from '#api/tags.queries.ts';
import type { ConfigOverride, ConfigOverrideKey, TagName } from '#dn-types/host.ts';

export type OverridePlusSource = { value: ConfigOverride; source: TagName | 'host' };
export type OverrideMap = Map<ConfigOverrideKey, OverridePlusSource>;

type Props = {
  hostConfigOverrides: ConfigOverride[];
  tagNames: TagName[];
};

export function useOverrideMaps({ hostConfigOverrides, tagNames }: Props) {
  // Fetch the tag details
  const allTags = useSuspenseMaxTagsQuery().data.data;
  const tags = allTags.filter((t) => tagNames.includes(t.name));

  return useMemo(() => {
    // Set up containers
    const configMap = new Map<ConfigOverrideKey, OverridePlusSource>();
    const statsMap = new Map<ConfigOverrideKey, OverridePlusSource>();

    // Sort tags by priority order, ascending (different from how we display them)
    const sortedTags = [...tags].sort((a, b) => a.priority - b.priority);

    // Loop tags and set them in the map.  Higher priority will overwrite lower
    for (const tag of sortedTags) {
      for (const o of tag.configOverrides) {
        if (o.key.startsWith('stats.')) {
          statsMap.set(o.key, { value: o, source: tag.name });
        } else {
          configMap.set(o.key, { value: o, source: tag.name });
        }
      }
    }

    // Add in any host overrides, which take prescience over all tags
    for (const hostConfig of hostConfigOverrides) {
      if (hostConfig.key.startsWith('stats.')) {
        statsMap.set(hostConfig.key, { value: hostConfig, source: 'host' });
      } else {
        configMap.set(hostConfig.key, { value: hostConfig, source: 'host' });
      }
    }

    return { statsMap, configMap };
  }, [hostConfigOverrides, tags]);
}
